import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { FileText, Download, CalendarDays, TrendingUp } from 'lucide-react';
import { getReports, downloadReportBlob, statementUrl } from '../lib/api';

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setReports(await getReports()); }
    catch (e) { console.error('Failed to load reports', e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReport = async (r) => {
    try {
      const blob = await downloadReportBlob(r.id);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Could not open the report'); }
  };

  return (
    <div className="min-h-screen bg-farmway-green pt-28">
      <div className="container-fw pb-24">
        <span className="eyebrow">Investor reports</span>
        <h1 className="mt-3 display-xl text-4xl text-farmway-cream sm:text-5xl">Reports & statements</h1>
        <p className="mt-3 max-w-2xl text-farmway-cream/70">
          Your monthly portfolio statements, archived automatically so you can download any period whenever you need it.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-farmway-gold/30 bg-farmway-gold/10 p-6">
          <div className="flex items-center gap-4">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-farmway-gold text-farmway-green"><FileText className="h-5 w-5" /></span>
            <div>
              <p className="font-display font-bold text-farmway-cream">Current statement</p>
              <p className="text-xs text-farmway-cream/60">A live snapshot of your portfolio as of today.</p>
            </div>
          </div>
          <a data-testid="current-statement-btn" href={statementUrl()} target="_blank" rel="noopener noreferrer" className="btn-gold">
            <Download className="h-4 w-4" /> Download now
          </a>
        </div>

        <div data-tour="reports-list" data-testid="reports-list" className="mt-8">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-farmway-cream">
            <CalendarDays className="h-5 w-5 text-farmway-gold" /> Monthly archive
          </h2>
          {loading ? (
            <p className="mt-6 text-farmway-cream/50">Loading…</p>
          ) : reports.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-white/15 bg-farmway-green-mid/20 p-12 text-center">
              <p className="text-farmway-cream/60">No archived statements yet. Statements are archived automatically on the 1st of each month.</p>
            </div>
          ) : (
            <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
              <div className="hidden grid-cols-5 gap-4 bg-farmway-green-mid/40 px-6 py-3 text-[11px] uppercase tracking-wider text-farmway-cream/50 sm:grid">
                <span className="col-span-2">Period</span><span>Invested</span><span>Value</span><span className="text-right">Download</span>
              </div>
              {reports.map((r) => (
                <div key={r.id} className="grid grid-cols-2 items-center gap-4 border-t border-white/10 px-6 py-4 sm:grid-cols-5">
                  <div className="col-span-2 flex items-center gap-3">
                    <FileText className="h-5 w-5 shrink-0 text-farmway-leaf-bright" />
                    <div>
                      <p className="text-sm font-medium text-farmway-cream">{r.periodLabel}</p>
                      <p className="flex items-center gap-1 font-display text-xs font-bold text-farmway-leaf-bright">
                        <TrendingUp className="h-3 w-3" />{r.returnPct >= 0 ? '+' : ''}{r.returnPct}%
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-farmway-cream/80">{money(r.totalInvested)}</p>
                  <p className="text-sm text-farmway-cream/80">{money(r.currentValue)}</p>
                  <div className="text-right">
                    <button data-testid={`report-download-${r.id}`} onClick={() => openReport(r)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-farmway-cream/80 transition-colors hover:border-farmway-gold hover:text-farmway-gold">
                      <Download className="h-3.5 w-3.5" /> PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
